import { useEffect, useMemo, useState } from 'react'
import { Loader2, RefreshCw, X } from 'lucide-react'
import api from '../../utils/api'
import { formatInstallerDisplayName } from '../../utils/installerName'
import PackageSearchSelect from './PackageSearchSelect'

export default function PromotionLoginRecordsTab() {
  const [loading, setLoading] = useState(false)
  const [sourceOptions, setSourceOptions] = useState([])
  const [sourceLabels, setSourceLabels] = useState([])
  const [phone, setPhone] = useState('')
  const [deviceId, setDeviceId] = useState('')
  const [uniqueByUdid, setUniqueByUdid] = useState(true)
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [records, setRecords] = useState([])
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalPages: 1 })
  const [jumpPage, setJumpPage] = useState('')
  const [selectedRecord, setSelectedRecord] = useState(null)

  const fetchRecords = async (page = 1, filters = {}) => {
    const nextSourceLabels = Array.isArray(filters.sourceLabels) ? filters.sourceLabels : sourceLabels
    const nextPhone = typeof filters.phone === 'string' ? filters.phone : phone
    const nextDeviceId = typeof filters.deviceId === 'string' ? filters.deviceId : deviceId
    const nextStartDate = typeof filters.startDate === 'string' ? filters.startDate : startDate
    const nextEndDate = typeof filters.endDate === 'string' ? filters.endDate : endDate
    const nextUniqueByUdid = typeof filters.uniqueByUdid === 'boolean' ? filters.uniqueByUdid : uniqueByUdid

    setLoading(true)
    try {
      const res = await api.get('/admin/promotion-login-records', {
        params: {
          page,
          limit: pagination.limit,
          sourceLabels: nextSourceLabels.length > 0 ? nextSourceLabels : undefined,
          phone: nextPhone || undefined,
          deviceId: nextDeviceId || undefined,
          startDate: nextStartDate || undefined,
          endDate: nextEndDate || undefined,
          uniqueByUdid: nextUniqueByUdid ? '1' : undefined
        }
      })
      if (res.data.success) {
        const data = res.data.data || res.data || {}
        const pager = data.pagination || {}
        setRecords(data.list || [])
        setSourceOptions(data.source_options || [])
        setPagination({
          page: pager.page || 1,
          limit: pager.limit || pagination.limit,
          total: pager.total || 0,
          totalPages: pager.totalPages || 1
        })
      }
    } catch (error) {
      console.error('获取渠道归属明细失败:', error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchRecords(1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearch = (e) => {
    e.preventDefault()
    fetchRecords(1)
  }

  const handleReset = () => {
    setSourceLabels([])
    setPhone('')
    setDeviceId('')
    setUniqueByUdid(true)
    setStartDate('')
    setEndDate('')
    setJumpPage('')
    fetchRecords(1, {
      sourceLabels: [],
      phone: '',
      deviceId: '',
      uniqueByUdid: true,
      startDate: '',
      endDate: ''
    })
  }

  const handleJump = () => {
    const page = Number(jumpPage)
    if (!jumpPage.trim() || isNaN(page)) {
      setJumpPage('')
      return
    }
    if (page < 1 || page > pagination.totalPages) {
      alert(`请输入 1 ~ ${pagination.totalPages} 之间的页码`)
      return
    }
    fetchRecords(page)
    setJumpPage('')
  }

  const formattedComputerInfo = useMemo(() => {
    if (!selectedRecord?.computer_info) return '-'
    try {
      return JSON.stringify(JSON.parse(selectedRecord.computer_info), null, 2)
    } catch (error) {
      return selectedRecord.computer_info
    }
  }, [selectedRecord])

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">渠道归属明细(CPA)</h2>
          <p className="mt-1 text-sm text-gray-500">按注册时首次绑定渠道归因，每个用户一行，与渠道新增用户统计口径一致</p>
        </div>
        <form onSubmit={handleSearch} className="flex flex-wrap items-center gap-2">
          <PackageSearchSelect
            value={sourceLabels}
            onChange={setSourceLabels}
            options={sourceOptions}
            multiple
            placeholder="全部渠道"
            searchPlaceholder="搜索渠道名"
            className="min-w-[240px]"
          />
          <input
            type="text"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="手机号"
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <input
            type="text"
            value={deviceId}
            onChange={(e) => setDeviceId(e.target.value)}
            placeholder="设备ID号"
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <label
            className="inline-flex cursor-pointer select-none items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-700"
            title="同一台设备仅展示最先绑定的一条记录"
          >
            <input
              type="checkbox"
              checked={uniqueByUdid}
              onChange={(e) => setUniqueByUdid(e.target.checked)}
              className="h-4 w-4"
            />
            设备Udid唯一
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            title="归属日期（注册日期）起"
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            title="归属日期（注册日期）止"
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
          />
          <button type="submit" className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white hover:bg-blue-700">
            查询
          </button>
          <button type="button" onClick={handleReset} className="rounded-lg bg-gray-100 px-4 py-2 text-sm text-gray-700 hover:bg-gray-200">
            重置
          </button>
          <button
            type="button"
            onClick={() => fetchRecords(pagination.page)}
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            刷新
          </button>
        </form>
      </div>

      {loading ? (
        <div className="flex items-center justify-center rounded-xl border border-gray-200 bg-white py-12">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">用户ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">手机号</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">渠道标签</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">归属日期</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">首条安装包</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">首次绑定时间</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">首条设备ID</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">版本</th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {records.length > 0 ? (
                  records.map((item, index) => {
                    const displayInstallerName = formatInstallerDisplayName(item.first_installer_name)

                    return (
                      <tr key={`${item.user_id}-${index}`} className="hover:bg-gray-50">
                        <td className="px-6 py-4 text-sm text-gray-900">{item.user_id || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">{item.phone || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="max-w-[200px] truncate" title={item.source_label || undefined}>
                            {item.source_label || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{item.stat_date || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <div className="max-w-[220px] truncate" title={displayInstallerName || undefined}>
                            {displayInstallerName || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{item.source_bound_at || '-'}</td>
                        <td className="px-6 py-4 font-mono text-sm text-gray-900">
                          <div className="max-w-[240px] truncate" title={item.device_id || undefined}>
                            {item.device_id || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900">{item.version || '-'}</td>
                        <td className="px-6 py-4 text-sm text-gray-900">
                          <button
                            type="button"
                            onClick={() => setSelectedRecord(item)}
                            className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-1 text-xs font-medium text-blue-700 hover:bg-blue-100"
                          >
                            详情
                          </button>
                        </td>
                      </tr>
                    )
                  })
                ) : (
                  <tr>
                    <td colSpan={9} className="px-6 py-10 text-center text-sm text-gray-500">
                      暂无数据
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="flex items-center justify-between bg-gray-50 px-6 py-4">
            <div className="text-sm text-gray-600">
              共 {pagination.total} 条，第 {pagination.page}/{pagination.totalPages} 页
            </div>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={jumpPage}
                onChange={(e) => setJumpPage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleJump()}
                placeholder="页码"
                min={1}
                max={pagination.totalPages}
                className="w-16 rounded-lg border border-gray-300 px-2 py-1 text-sm text-center focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
              <button
                onClick={handleJump}
                className="rounded-lg border border-gray-300 px-3 py-1 text-sm hover:bg-gray-100 dark:border-gray-600 dark:hover:bg-gray-700"
              >
                跳转
              </button>
              <button
                disabled={pagination.page <= 1}
                onClick={() => fetchRecords(pagination.page - 1)}
                className="rounded-lg border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                上一页
              </button>
              <button
                disabled={pagination.page >= pagination.totalPages}
                onClick={() => fetchRecords(pagination.page + 1)}
                className="rounded-lg border border-gray-300 px-3 py-1 disabled:opacity-50"
              >
                下一页
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedRecord ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-4xl rounded-2xl bg-white shadow-2xl">
            <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">归属详情 #{selectedRecord.user_id || '-'}</h3>
                <p className="mt-1 text-sm text-gray-500">
                  归属日期 {selectedRecord.stat_date || '-'} · 首次绑定 {selectedRecord.source_bound_at || '-'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setSelectedRecord(null)}
                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="grid gap-6 px-6 py-6 md:grid-cols-2">
              <div className="grid grid-cols-2 gap-4 rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm">
                <div>
                  <div className="text-gray-500">用户ID</div>
                  <div className="mt-1 font-medium text-gray-900">{selectedRecord.user_id || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">手机号</div>
                  <div className="mt-1 font-medium text-gray-900">{selectedRecord.phone || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">渠道标签</div>
                  <div className="mt-1 font-medium text-gray-900">{selectedRecord.source_label || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">归属日期</div>
                  <div className="mt-1 font-medium text-gray-900">{selectedRecord.stat_date || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">首次绑定时间</div>
                  <div className="mt-1 font-medium text-gray-900">{selectedRecord.source_bound_at || '-'}</div>
                </div>
                <div>
                  <div className="text-gray-500">版本</div>
                  <div className="mt-1 font-medium text-gray-900">{selectedRecord.version || '-'}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-gray-500">首条安装包</div>
                  <div className="mt-1 break-all font-medium text-gray-900">{formatInstallerDisplayName(selectedRecord.first_installer_name) || '-'}</div>
                </div>
                <div className="col-span-2">
                  <div className="text-gray-500">首条设备ID</div>
                  <div className="mt-1 break-all font-medium text-gray-900">{selectedRecord.device_id || '-'}</div>
                </div>
              </div>

              <div>
                <div className="mb-2 text-sm font-medium text-gray-700">computer_info</div>
                <div className="max-h-[420px] overflow-auto rounded-xl border border-gray-200 bg-gray-50 p-4">
                  <pre className="whitespace-pre-wrap break-all text-xs leading-6 text-gray-800">{formattedComputerInfo}</pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
